import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getParSuggestions, acceptParSuggestion, ParSuggestion } from './api';
import './inventory-v2.css';
import DataTable from '../../shared/components/DataTable';

const ParSuggestions: React.FC = () => {
  const { sessionToken, user } = useAuth();
  const [suggestions, setSuggestions] = useState<ParSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [config, setConfig] = useState({ coverage_days: 7, lead_time_days: 0, safety_stock_pct: 0.2 });
  const [accepting, setAccepting] = useState<number | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    getParSuggestions(sessionToken, config)
      .then(res => setSuggestions(res.suggestions))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [sessionToken, config]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (itemId: number, par: number) => {
    setAccepting(itemId);
    try {
      await acceptParSuggestion(sessionToken, itemId, par);
      setSuggestions(prev => prev.map(s =>
        s.item_id === itemId ? { ...s, current_par: par } : s
      ));
    } catch (e: any) {
      setError(e.message);
    } finally {
      setAccepting(null);
    }
  };

  const canWrite = ['Owner', 'Admin', 'Manager'].includes(user?.role || '');

  return (
    <div className="inv2-page">
      <div className="inv2-header">
        <h1>🎯 Par Level Suggestions</h1>
      </div>

      {error && <div className="inv2-error">{error}</div>}

      {/* Configuration */}
      <div className="inv2-section">
        <h3>Configuration</h3>
        <div className="inv2-form-grid">
          <div className="inv2-form-group">
            <label>Coverage Days</label>
            <input type="number" value={config.coverage_days} onChange={e => setConfig(p => ({ ...p, coverage_days: Number(e.target.value) }))} className="inv2-input" style={{ width: '100px' }} />
          </div>
          <div className="inv2-form-group">
            <label>Lead Time Days</label>
            <input type="number" value={config.lead_time_days} onChange={e => setConfig(p => ({ ...p, lead_time_days: Number(e.target.value) }))} className="inv2-input" style={{ width: '100px' }} />
          </div>
          <div className="inv2-form-group">
            <label>Safety Stock %</label>
            <input type="number" step="0.05" value={config.safety_stock_pct} onChange={e => setConfig(p => ({ ...p, safety_stock_pct: Number(e.target.value) }))} className="inv2-input" style={{ width: '100px' }} />
          </div>
          <div className="inv2-form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button onClick={load} disabled={loading} className="inv2-btn inv2-btn-primary">
              {loading ? 'Loading...' : 'Recalculate'}
            </button>
          </div>
        </div>
        <p className="inv2-hint">
          Formula: Par = Avg Daily Usage × (Coverage + Lead Time) × (1 + Safety %)
        </p>
      </div>

      {loading ? (
        <div className="inv2-loading">Calculating suggestions...</div>
      ) : (
        <div className="inv2-table-container">
          <DataTable
            rows={suggestions}
            rowKey={(s) => s.item_id}
            caption="Par level suggestions"
            moreLabel="Usage history"
            empty="No suggestions available. Add stock movements to generate suggestions."
            columns={[
              {
                key: 'item', header: 'Item', priority: 'primary',
                render: (s) => (
                  <>
                    <strong>{s.item_name}</strong>
                    {s.brand && <span className="inv2-brand"> ({s.brand})</span>}
                    <div className="inv2-subtitle">{s.supplier_name || ''}</div>
                  </>
                ),
              },
              { key: 'suggested', header: 'Suggested Par', priority: 'summary', numeric: true, render: (s) => <span className="inv2-big-value">{s.suggested_par ?? '—'}</span> },
              { key: 'current', header: 'Current Par', priority: 'summary', numeric: true, render: (s) => s.current_par ?? '—' },
              {
                key: 'diff', header: 'Difference', priority: 'summary', numeric: true,
                render: (s) => (
                  <span className={s.difference != null && s.difference > 0 ? 'inv2-text-warning' : undefined}>
                    {s.difference != null ? (s.difference > 0 ? `+${s.difference}` : s.difference) : '—'}
                  </span>
                ),
              },
              { key: 'onhand', header: 'On Hand', priority: 'summary', numeric: true, render: (s) => `${Number(s.on_hand).toFixed(1)} ${s.base_unit}` },
              { key: 'confidence', header: 'Confidence', priority: 'detail', render: (s) => <span className={`inv2-confidence inv2-confidence-${s.confidence}`}>{s.confidence}</span> },
              { key: 'u7', header: '7-Day Usage', priority: 'detail', numeric: true, render: (s) => (s.usage_7_days > 0 ? Number(s.usage_7_days).toFixed(1) : '—') },
              { key: 'u30', header: '30-Day Usage', priority: 'detail', numeric: true, render: (s) => (s.usage_30_days > 0 ? Number(s.usage_30_days).toFixed(1) : '—') },
              {
                key: 'days', header: 'Days Left', priority: 'detail', numeric: true,
                render: (s) => (
                  <span className={s.days_remaining != null && s.days_remaining < 7 ? 'inv2-text-danger' : undefined}>
                    {s.days_remaining != null ? `${s.days_remaining}d` : '—'}
                  </span>
                ),
              },
              { key: 'category', header: 'Category', priority: 'detail', render: (s) => s.category_name || '—' },
            ]}
            actions={canWrite ? (s) => (
              s.suggested_par != null && s.suggested_par !== s.current_par ? (
                <button
                  onClick={() => handleAccept(s.item_id, s.suggested_par!)}
                  disabled={accepting === s.item_id}
                  className="inv2-btn inv2-btn-sm inv2-btn-primary"
                >
                  {accepting === s.item_id ? '...' : 'Accept'}
                </button>
              ) : null
            ) : undefined}
          />
        </div>
      )}
    </div>
  );
};

export default ParSuggestions;
